import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * The states of the contact list other than « rows » (W4, B2-liste, DES-12):
 *   loading  → skeleton rows shaped like the table (the first read has not answered)
 *   error    → translated message, code in small monospace, « Réessayer »
 *   empty    → nothing in this view yet, exactly one CTA
 *   filtered → contacts exist but the filters or the search hide them all, « Effacer les filtres »
 *   allCalled → the list has contacts, every one already called: they wait
 *               under their result (« Tout afficher »), nothing to import
 * The empty state used to say « Importez une liste » even when a search hid
 * everything, with no button (QW-d10).
 */
export type ContactsListStateKind =
  | { kind: 'loading' }
  | { kind: 'error'; code?: string; onRetry: () => void }
  | { kind: 'empty'; view: 'default' | 'callbacks' | 'archived'; onAction: () => void }
  | { kind: 'filtered'; onClear: () => void }
  | { kind: 'allCalled'; onShowAll: () => void };

export function ContactsListState(props: ContactsListStateKind) {
  const { t } = useTranslation(['dialer', 'common']);
  if (props.kind === 'loading') {
    return (
      <div role="status" aria-live="polite" className="rounded-vf-lg overflow-hidden" style={{ border: '1px solid var(--vf-border)', background: 'var(--vf-bg)' }}>
        <span className="sr-only">{t('dialer:contacts.empty.loading')}</span>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} data-testid="contacts-skeleton-row" className="flex items-center gap-4 px-4 h-11" style={{ borderBottom: '1px solid var(--vf-border)' }}>
            <div className="h-3.5 w-3.5 rounded-sm animate-pulse motion-reduce:animate-none" style={{ background: 'var(--vf-surface-3)' }} />
            <div className="h-3 w-20 rounded animate-pulse motion-reduce:animate-none" style={{ background: 'var(--vf-surface-3)' }} />
            <div className="h-3 w-40 rounded animate-pulse motion-reduce:animate-none" style={{ background: 'var(--vf-surface-3)' }} />
            <div className="h-3 w-28 rounded animate-pulse motion-reduce:animate-none" style={{ background: 'var(--vf-surface-3)' }} />
            <div className="h-3 w-24 rounded animate-pulse motion-reduce:animate-none hidden md:block" style={{ background: 'var(--vf-surface-3)' }} />
          </div>
        ))}
      </div>
    );
  }
  const box = 'rounded-vf-lg px-6 py-12 text-center flex flex-col items-center gap-2';
  const boxStyle = { border: '1px solid var(--vf-border)', background: 'var(--vf-bg)' };
  if (props.kind === 'error') {
    return (
      <div role="alert" className={box} style={boxStyle}>
        <p className="text-[14px] font-medium" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:contacts.empty.errorTitle')}</p>
        {props.code && <p className="font-mono text-[11px]" style={{ color: 'var(--vf-text-3)' }}>{props.code}</p>}
        <Button type="button" variant="outline" size="sm" className="mt-1" onClick={props.onRetry}>{t('common:actions.retry')}</Button>
      </div>
    );
  }
  if (props.kind === 'filtered') {
    return (
      <div className={box} style={boxStyle}>
        <p className="text-[14px] font-medium" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:contacts.empty.filteredTitle')}</p>
        <p className="text-[13px]" style={{ color: 'var(--vf-text-3)' }}>{t('dialer:contacts.empty.filteredHint')}</p>
        <Button type="button" variant="outline" size="sm" className="mt-1" onClick={props.onClear}>{t('dialer:contacts.empty.clearFilters')}</Button>
      </div>
    );
  }
  if (props.kind === 'allCalled') {
    return (
      <div className={box} style={boxStyle}>
        <p className="text-[14px] font-medium" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:contacts.empty.allCalledTitle')}</p>
        <p className="text-[13px]" style={{ color: 'var(--vf-text-3)' }}>{t('dialer:contacts.empty.allCalledHint')}</p>
        <Button type="button" variant="outline" size="sm" className="mt-1" onClick={props.onShowAll}>{t('dialer:contacts.filters.callStatus.showAll')}</Button>
      </div>
    );
  }
  const byView = {
    default: { titleKey: 'dialer:table.noProspects', hintKey: 'dialer:table.noProspectsHint', actionKey: 'dialer:contacts.empty.addContacts' },
    callbacks: { titleKey: 'dialer:contacts.empty.noCallbacks', hintKey: 'dialer:contacts.empty.noCallbacksHint', actionKey: 'dialer:contacts.banner.backToCallList' },
    archived: { titleKey: 'dialer:contacts.empty.noArchived', hintKey: 'dialer:contacts.empty.noArchivedHint', actionKey: 'dialer:contacts.banner.backToCallList' },
  }[props.view];
  return (
    <div className={box} style={boxStyle}>
      <Users className="w-10 h-10 mb-1" style={{ color: 'var(--vf-text-4)' }} aria-hidden />
      <p className="text-[14px] font-medium" style={{ color: 'var(--vf-text-1)' }}>{t(byView.titleKey)}</p>
      <p className="text-[13px]" style={{ color: 'var(--vf-text-3)' }}>{t(byView.hintKey)}</p>
      <Button type="button" size="sm" className="mt-1" onClick={props.onAction}>{t(byView.actionKey)}</Button>
    </div>
  );
}
